import { useEffect, useRef, useState, type FormEvent, type ReactNode } from "react";
import { Button, Dialog } from "@omnabase/ui";
import { authClient } from "../../lib/auth";
import { loadTurnstile, turnstileToken } from "../../lib/turnstile";
import { isCode, normaliseCode, problemFor } from "../../lib/signin";
import { isReauth } from "../../lib/admin";
import { toast } from "../../shell/toast";

/** The reauth rule's dialog (M6 round four): a change answered 404 `reauth` (since the one-card round, 2026-10-23: the last code is more than
 *  eight hours old; reading needs none but the owners' lists), so a new code is mailed to the administrator's own address as the dialog
 *  opens, and the dialog names what it protects (`what`); Confirm signs in afresh (a new session) and the change is sent again by the caller.
 *  A stolen cookie never gets past here. */
export function ReauthDialog({ email, what, onConfirmed, onClose }: { email: string; /** the switch or the workspace the code is for */ what?: string; onConfirmed: () => void; onClose: () => void }) {
  const [proof, setProof] = useState(""); const [problem, setProblem] = useState<string | null>(null); const [busy, setBusy] = useState(false); const [sent, setSent] = useState(false);
  const turnstileEl = useRef<HTMLDivElement>(null);
  useEffect(() => {
    let gone = false;
    (async () => {
      await loadTurnstile();
      const token = turnstileEl.current ? await turnstileToken(turnstileEl.current) : undefined;
      const r = await authClient.emailOtp.sendVerificationOtp({ email, type: "sign-in", fetchOptions: { headers: token ? { "x-turnstile-token": token } : {} } });
      if (gone) return;
      if (r.error) setProblem(problemFor(r.error.status, r.error.code)); else setSent(true);
    })();
    return () => { gone = true; };
  }, [email]);
  const confirm = async (e: FormEvent) => {
    e.preventDefault();
    if (!isCode(proof)) { setProblem("Enter the six digits from the mail."); return; }
    setProblem(null); setBusy(true);
    const r = await authClient.signIn.emailOtp({ email, otp: normaliseCode(proof) });
    setBusy(false);
    if (r.error) { setProblem(problemFor(r.error.status, r.error.code)); return; }
    onConfirmed();
  };
  return (
    <Dialog title="Confirm it is you" intro={`${what ? `${what}: this` : "This"} needs a fresh sign-in, once in eight hours. ${sent ? `A code went to ${email}.` : `Sending a code to ${email}…`}`} onClose={onClose}
      footer={<><Button onClick={onClose}>Cancel</Button><Button variant="primary" disabled={busy || !sent} onClick={(e) => void confirm(e as unknown as FormEvent)}>Confirm</Button></>}>
      <form onSubmit={(e) => void confirm(e)}>
        <div className="ob-dialog__field"><span className="ob-dialog__label">Code</span><input className="ob-dialog__input" value={proof} onChange={(e) => setProof(e.target.value)} inputMode="numeric" autoComplete="one-time-code" placeholder="123 456" aria-label="Code" autoFocus /></div>
        <div ref={turnstileEl} className="ob-signin__turnstile" />
        {problem && <p className="ob-signin__problem" role="alert">{problem}</p>}
      </form>
    </Dialog>
  );
}

/** A change that may answer `reauth`: run it; on that answer keep it, show the dialog naming `what`, and run it again once the code is in.
 *  Answers the guard and the dialog to render. */
export function useFreshCode(email: string): { guard: (act: () => Promise<void>, what: string) => Promise<void>; dialog: ReactNode } {
  const [pending, setPending] = useState<{ act: () => Promise<void>; what: string } | null>(null);
  const guard = async (act: () => Promise<void>, what: string) => {
    try { await act(); } catch (e) { if (isReauth(e)) setPending({ act, what }); else toast((e as Error).message, { kind: "error" }); }
  };
  const dialog = pending && <ReauthDialog email={email} what={pending.what} onClose={() => setPending(null)} onConfirmed={() => { const p = pending; setPending(null); void guard(p.act, p.what); }} />;
  return { guard, dialog };
}
